import {createSelector, MemoizedSelector} from '@ngrx/store';
import {AppState} from '../states/app.state';
import {BelegVersandstatusDTO} from '../../openapi/fakturierung-openapi';


/**
 * Selector-Factories fuer die Versandstatus-Historie eines Belegs.
 *
 * Die belegId-spezifischen Selectoren werden in einer Map gecached,
 * damit jeder Aufruf mit derselben belegId denselben memoized Selector
 * liefert. Ohne den Cache wuerden parallele Konsumenten (Dialog + Preview)
 * jeweils einen neuen `createSelector` erzeugen und der NgRx-Memoize bricht.
 *
 * Der Cache ist auf {@link MAX_CACHE_SIZE} begrenzt und nutzt eine
 * LRU-Strategie (re-insert bei Hit + drop des ältesten Eintrags beim
 * Überlauf). Bei Betriebs-Wechsel wird der Cache vom
 * {@code VersandstatusHistoryCacheCleanupEffects}-Effect via
 * {@link clearAll} geleert, damit keine MemoizedSelectors fuer Belege
 * fremder Betriebe akkumulieren.
 */
export class VersandstatusHistorySelectors {

  private static slice = (state: AppState) => state.versandstatusHistory;

  private static readonly MAX_CACHE_SIZE = 200;

  private static _historieByBelegIdCache = new Map<string, MemoizedSelector<AppState, BelegVersandstatusDTO[]>>();
  private static _loadingByBelegIdCache = new Map<string, MemoizedSelector<AppState, boolean>>();
  private static _totalElementsByBelegIdCache = new Map<string, MemoizedSelector<AppState, number>>();
  private static _isLoadedByBelegIdCache = new Map<string, MemoizedSelector<AppState, boolean>>();
  private static _hasErrorByBelegIdCache = new Map<string, MemoizedSelector<AppState, boolean>>();

  /**
   * LRU-Cache-Helper. Bei Hit wird der Eintrag im Insertion-Order
   * ganz nach hinten verschoben (so dass der Eintrag, der zuerst eingefuegt
   * und nicht mehr angefragt wurde, ueber {@link Map#keys} als erstes
   * gedroppt wird, sobald {@link MAX_CACHE_SIZE} ueberschritten ist).
   */
  private static getOrCreate<T>(
    cache: Map<string, T>,
    key: string,
    factory: () => T,
  ): T {
    if (cache.has(key)) {
      const existing = cache.get(key)!;
      // INFO: LRU-Touch — re-insert verschiebt den Eintrag ans Ende der
      // Insertion-Order, ohne den Wert (und damit die Memoization) zu verlieren.
      cache.delete(key);
      cache.set(key, existing);
      return existing;
    }
    const created = factory();
    cache.set(key, created);
    if (cache.size > this.MAX_CACHE_SIZE) {
      const oldest = cache.keys().next().value;
      if (oldest !== undefined) {
        cache.delete(oldest);
      }
    }
    return created;
  }

  public static versandstatusHistorieByBelegId = (
    belegId: string,
  ): MemoizedSelector<AppState, BelegVersandstatusDTO[]> => {
    return this.getOrCreate(this._historieByBelegIdCache, belegId, () =>
      createSelector(
        this.slice,
        slice => slice?.byBelegId?.[belegId]?.content ?? [],
      ),
    );
  };

  public static versandstatusHistorieLoadingByBelegId = (
    belegId: string,
  ): MemoizedSelector<AppState, boolean> => {
    return this.getOrCreate(this._loadingByBelegIdCache, belegId, () =>
      createSelector(
        this.slice,
        slice => !!slice?.byBelegId?.[belegId]?.loading,
      ),
    );
  };

  public static versandstatusHistorieTotalElementsByBelegId = (
    belegId: string,
  ): MemoizedSelector<AppState, number> => {
    return this.getOrCreate(this._totalElementsByBelegIdCache, belegId, () =>
      createSelector(
        this.slice,
        slice => slice?.byBelegId?.[belegId]?.totalElements ?? 0,
      ),
    );
  };

  /**
   * Liefert `true`, wenn die Historie fuer diesen Beleg bereits im Store
   * abgelegt wurde (egal ob mit Inhalt oder leer). Wird vom Reload-Effect
   * benutzt, um Reloads nur fuer aktive Subscriptions auszuloesen.
   */
  public static isLoadedForBeleg = (
    belegId: string,
  ): MemoizedSelector<AppState, boolean> => {
    return this.getOrCreate(this._isLoadedByBelegIdCache, belegId, () =>
      createSelector(
        this.slice,
        slice => !!slice?.byBelegId?.[belegId],
      ),
    );
  };

  /**
   * Liefert `true`, wenn der letzte Lade-Versuch der Historie fuer diesen
   * Beleg fehlgeschlagen ist. Wird vom Preview/Dialog ausgewertet, um einen
   * UI-Hinweis statt einer leeren Liste zu rendern.
   */
  public static versandstatusHistorieHasErrorByBelegId = (
    belegId: string,
  ): MemoizedSelector<AppState, boolean> => {
    return this.getOrCreate(this._hasErrorByBelegIdCache, belegId, () =>
      createSelector(
        this.slice,
        slice => slice?.byBelegId?.[belegId]?.error != null,
      ),
    );
  };

  /**
   * Leert alle Selector-Caches. Wird vom Lifecycle-Cleanup-Effect bei
   * Betriebs-Wechsel aufgerufen und steht Tests als Reset-Hook zur Verfuegung.
   */
  public static clearAll(): void {
    this._historieByBelegIdCache.clear();
    this._loadingByBelegIdCache.clear();
    this._totalElementsByBelegIdCache.clear();
    this._isLoadedByBelegIdCache.clear();
    this._hasErrorByBelegIdCache.clear();
  }

}
